import { Container, Graphics, type Sprite } from 'pixi.js';
import type { ArenaFeedbackPriority } from '../performance/arenaPresentationBudget';
import { TILE_HALF } from '../sim/projection';
import { projectileFlightMs, MOLTEN_TINT } from './volleyFx';
import type { GroundEffectId, ImpactEffectId, ProjectileEffectId } from '../sim/content';
import type { EffectArt, EffectClip } from './effectsArt';
import { UoHueSprite } from './uoHueBatch';
import type { BloodTextures } from './bloodArt';

/**
 * The **Arena juice FX layer** (Arena Slice F, PRD #334, ADR-0178): pooled,
 * budgeted particle bursts on enemy deaths, projectile tracers from the runner to
 * each struck enemy, and expanding aura-pulse rings on aura weapon fire. The
 * {@link TelegraphOverlay}/{@link AutowalkOverlay} transient pattern: a child of
 * the renderer's camera-transformed `world`, aged each frame from the render
 * clock, `eventMode: 'none'` so it never swallows a pointer event.
 *
 * **Budgeted for ~100 concurrent enemies by construction**: every effect kind has
 * a hard pool cap ({@link FX_MAX_PARTICLES}/{@link FX_MAX_TRACERS}/
 * {@link FX_MAX_PULSES}); a spawn over the cap is silently dropped (a dense wave
 * loses a few flecks, never frame rate), and every `Graphics`/`Sprite` ever
 * created is retained in a free list and reused - after the first busy second
 * the layer allocates nothing per frame.
 *
 * **Since the fire-mage rework (ADR-0179/0180/0181)** the layer also plays real
 * baked UO effect strips - looping projectile flights (magic arrow / fireball,
 * rotated along the velocity) and one-shot impacts (explosion / flame strike) -
 * late-bound via {@link ArenaFxOverlay.setEffectArt} from the ADR-0180 bake
 * path, plus a tiny scheduler ({@link ArenaFxOverlay.schedule}) the volley
 * planner ({@link ./volleyFx}) stages cascades on. An absent strip (unbaked
 * host, warn-skipped effect) degrades per call to the original tinted
 * `Graphics` primitives - tracer for a flight, burst/pulse for an impact -
 * never a crash.
 *
 * Randomness (burst scatter only) comes from the injected `rng` (defaulting to
 * `Math.random` - this is the render layer, the {@link combatCue} precedent);
 * tests inject a scripted one.
 */

/** Hard cap on live burst particles across the whole layer (the ~100-enemy budget). */
export const FX_MAX_PARTICLES = 160;

/** Hard cap on live projectile tracers (an aura storm drops the excess). */
export const FX_MAX_TRACERS = 24;

/** Hard cap on live aura pulses (they fire on a throttled cadence anyway). */
export const FX_MAX_PULSES = 6;

/** Hard cap on live warrior crescent sweeps. */
export const FX_MAX_SWEEPS = 8;

/** Hard cap on live projectile effect sprites (a dense chain cascade drops the excess). */
export const FX_MAX_PROJECTILES = 24;

/** Hard cap on live one-shot impact sprites (explosions + flame strikes). */
export const FX_MAX_IMPACTS = 16;

/** Hard cap on live Hero buff presentations. */
export const FX_MAX_AEGIS = 4;

/** Aegis sparkle adjustment in screen-space pixels at the reference camera zoom. */
export const AEGIS_SPARKLE_OFFSET_Y = 5;

/** Hard cap on live looping ground fields (fire trails / telegraph sparkles, PRD #378). */
export const FX_MAX_FIELDS = 48;

/** Alpha a ground-field loop holds through its life (it sits under the action). */
export const FIELD_ALPHA = 0.9;

/** How long a degraded (no-strip) ground field's flicker cycle lasts, ms. */
export const FIELD_FLICKER_MS = 400;

/** Normal full-quality budget for queued not-yet-fired scheduled FX steps. */
export const FX_MAX_PENDING = 96;
/** Emergency headroom reserved for primary cues when the normal queue contains only primary work. */
export const FX_PRIMARY_PENDING_RESERVE = 64;

/**
 * Per-strip art-orientation offset added to a projectile's flight angle
 * (`atan2(dy,dx)`). UO's moving-effect art is authored at one fixed
 * orientation and rotated along the velocity by every classic client - but the
 * two strips are NOT authored the same way (confirmed by eye against the
 * captain's real bake): the magic arrow reads right at -3pi/4, while the
 * fireball flew ~45 degrees nose-down with that constant and wants a further
 * -pi/4. Tune by eye per strip; a wrong entry reads as a sideways projectile.
 */
export const PROJECTILE_ART_BASE_ANGLE_RAD: Record<ProjectileEffectId, number> = {
  'magic-arrow': (-3 * Math.PI) / 4,
  fireball: -Math.PI,
  // PRD #378 additions - tuned by eye at the captain's bake like the two above
  // (a wrong entry reads as a sideways projectile, never a crash). VERIFY.
  'fire-snake': -Math.PI,
  star: -Math.PI / 2, // the meteor falls straight down; the strip is authored upright. VERIFY
  // The archer's item-art flights (PRD #446 Slice 1, ADR-0237). ClassicUO draws a
  // fired arrow/bolt with `AngleToTarget = atan2(-offsetY, -offsetX)` on the
  // screen offset (`MovingEffect.UpdateOffset` -> `DrawStaticRotated`, no base
  // constant). britbank's screen projection is identical to CUO's ((x-y)*22,
  // (x+y)*22), and this overlay rotates by `atan2(dy, dx) + base`; since
  // `atan2(-dy,-dx) == atan2(dy,dx) + PI`, base = PI reproduces CUO's rotation
  // exactly (the same baked 0x0F42/0x1BFE item art points along its flight).
  arrow: Math.PI,
  bolt: Math.PI,
};

/** Extra sprite scale per Greater Projectile size tier above the first. */
export const PROJECTILE_TIER_SCALE = 0.15;

/** Flecks per ordinary enemy-death burst. */
export const BURST_PARTICLES = 8;

/** Flecks in the boss-death burst (the one big moment). */
export const BOSS_BURST_PARTICLES = 28;

/** How long one burst fleck lives (rise, fall, fade). */
export const PARTICLE_TTL_MS = 550;

/** How long a projectile tracer takes to cross from runner to target. */
export const TRACER_TTL_MS = 130;

/** How long an aura pulse ring takes to expand to its radius and fade. */
export const PULSE_TTL_MS = 350;

/** How long a warrior crescent sweep expands and fades. */
export const SWEEP_TTL_MS = 260;

/** How long the compact Piercing Thrust trail flashes. */
export const PIERCE_TTL_MS = 180;

/** One fast eight-facing spin, with enough tail for the ring to finish fading. */
export const WHIRLWIND_TTL_MS = 440;

/** Downward gravity on burst flecks, px/s^2 (they pop up, then rain down). */
const PARTICLE_GRAVITY = 260;

/** Burst fleck launch speed range, px/s. */
const PARTICLE_SPEED_MIN = 40;
const PARTICLE_SPEED_MAX = 130;

/** Fleck radius in px (a small square-ish spark at bank zoom). */
const PARTICLE_RADIUS = 2.2;

/** Tracer dot radius in px. */
const TRACER_RADIUS = 2.6;

/** zIndex within the sortable `world`: above the depth-ranked sprites, below the telegraph. */
const OVERLAY_Z = 999_000;

/** The four corners of a one-tile ground diamond (the 2:1 dimetric cell), origin-centred. */
const DIAMOND: number[] = [0, -TILE_HALF, TILE_HALF, 0, 0, TILE_HALF, -TILE_HALF, 0];

/** The molten fallback palette: lava-bright fill over a darker crust rim. */
const MOLTEN_BLOB_COLOR = MOLTEN_TINT;
const MOLTEN_BLOB_DARK = 0xb34a10;

/**
 * The Graphics-primitive fallback for an unbaked molten splat (PRD #419 slice
 * B): a few overlapping lava-coloured circles, deterministically varied by the
 * splat's `art` index - the {@link ./bloodLayer} blob convention in the molten
 * palette. Centred on the origin (positioned on the tile base by the caller).
 */
function drawMoltenBlob(g: Graphics, art: number): void {
  const seed = (art * 2654435761) >>> 0;
  const rand = (n: number): number => ((seed ^ (n * 0x9e3779b1)) >>> 0) / 0xffffffff;
  const blobs = 3 + (art % 3); // 3..5 overlapping circles
  for (let i = 0; i < blobs; i++) {
    const r = 4 + rand(i * 2) * 5;
    const ox = (rand(i * 2 + 1) - 0.5) * 14;
    const oy = (rand(i * 2 + 3) - 0.5) * 8; // squashed vertically (dimetric ground)
    g.circle(ox, oy, r).fill({ color: i === 0 ? MOLTEN_BLOB_COLOR : MOLTEN_BLOB_DARK, alpha: 0.85 });
  }
}

/** A world-local point (pre-camera pixels, the space sprites are positioned in). */
export interface FxPoint {
  x: number;
  y: number;
}

interface Particle {
  gfx: Graphics;
  vx: number;
  vy: number;
  ageMs: number;
}

interface Tracer {
  gfx: Graphics;
  from: FxPoint;
  to: FxPoint;
  ageMs: number;
}

interface Pulse {
  gfx: Graphics;
  /** Target scale: an aura of Chebyshev radius `r` reads as a ~`2r`-tile diamond. */
  targetScale: number;
  ageMs: number;
}

interface Sweep {
  gfx: Graphics;
  startRotation: number;
  rotationDelta: number;
  scaleYRatio: number;
  scaleFrom?: number;
  scaleTo?: number;
  ttlMs: number;
  ageMs: number;
  liveTarget?: () => FxPoint | undefined;
}

/** A live projectile effect sprite: lerped flight + looping frame strip. */
interface ProjectileFx {
  sprite: UoHueSprite;
  clip: EffectClip;
  from: FxPoint;
  to: FxPoint;
  flightMs: number;
  /** Uniform art scale (the Greater Projectile size tier). */
  artScale: number;
  frameIndex: number;
  ageMs: number;
  kind: ProjectileEffectId;
  /**
   * Optional live-target provider (ADR-0216): re-resolves the struck enemy's
   * current world-local point each frame so the tracer homes onto a mover and
   * lands on it. Returns `undefined` once the target is gone (dead / off-screen),
   * after which the flight coasts to its last-known `to`.
   */
  liveTarget?: () => FxPoint | undefined;
}

/** A live one-shot impact sprite: frames play once, then release. */
interface ImpactFx {
  sprite: UoHueSprite;
  clip: EffectClip;
  ttlMs: number;
  frameIndex: number;
  ageMs: number;
}

interface AegisFx {
  key?: string;
  sparkle: UoHueSprite;
  sparkleClip: EffectClip;
  ttlMs: number;
  ageMs: number;
  sparkleFrameIndex: number;
  liveTarget?: () => FxPoint | undefined;
}

/** A live looping ground field: the strip loops in place until `ttlMs`, then releases.
 * `sprite`/`clip` are null in degrade mode (a flickering tile-diamond `gfx` instead). */
interface FieldFx {
  sprite: UoHueSprite | null;
  clip: EffectClip | null;
  gfx: Graphics | null;
  ttlMs: number;
  frameIndex: number;
  ageMs: number;
}

/** A scheduled FX step, fired when the overlay clock reaches `atMs`. */
interface PendingFx {
  atMs: number;
  fire: () => void;
  priority: ArenaFeedbackPriority;
}

export class ArenaFxOverlay extends Container {
  private readonly particles: Particle[] = [];
  private readonly tracers: Tracer[] = [];
  private readonly pulses: Pulse[] = [];
  private readonly sweeps: Sweep[] = [];
  private readonly freeParticles: Graphics[] = [];
  private readonly freeTracers: Graphics[] = [];
  private readonly freePulses: Graphics[] = [];
  private readonly freeSweeps: Graphics[] = [];
  private readonly projectiles: ProjectileFx[] = [];
  private readonly impacts: ImpactFx[] = [];
  private readonly aegises: AegisFx[] = [];
  private readonly fields: FieldFx[] = [];
  private readonly freeFieldGfx: Graphics[] = [];
  /** One shared sprite free list: frames retexture on acquire, so kinds can mix. */
  private readonly freeSprites: UoHueSprite[] = [];
  private readonly pending: PendingFx[] = [];
  private pendingLimit = FX_MAX_PENDING;
  /** The scheduler clock, advanced only by {@link update} (render time, ms). */
  private clockMs = 0;
  /** Late-bound baked effect strips; empty until {@link setEffectArt} (degrade mode). */
  private effectArt: EffectArt = {};
  /** Late-bound baked blood statics for the molten ground look (PRD #419 slice B);
   * empty until {@link setBloodArt} (degrade mode - the Graphics splat blob). */
  private bloodTextures: BloodTextures = new Map();
  /** Late-bound blaze hue for the molten splat. */
  private moltenHue = 0;
  private readonly rng: () => number;

  constructor(rng: () => number = Math.random) {
    super();
    this.rng = rng;
    // Pure visual feedback: never intercept pointer events; draw above the
    // depth-ranked world sprites (the camera transform still applies).
    this.eventMode = 'none';
    this.zIndex = OVERLAY_Z;
  }

  /** Live burst flecks (test/e2e observability). */
  get activeParticleCount(): number {
    return this.particles.length;
  }

  /** Live projectile tracers (test/e2e observability). */
  get activeTracerCount(): number {
    return this.tracers.length;
  }

  /** Live aura pulses (test/e2e observability). */
  get activePulseCount(): number {
    return this.pulses.length;
  }

  /** Live warrior crescent sweeps (test/e2e observability). */
  get activeSweepCount(): number {
    return this.sweeps.length;
  }

  /** Live projectile effect sprites (test/e2e observability). */
  get activeProjectileCount(): number {
    return this.projectiles.length;
  }

  /** Live one-shot impact sprites (test/e2e observability). */
  get activeImpactCount(): number {
    return this.impacts.length;
  }

  /** Live Warrior Aegis presentations (test/e2e observability). */
  get activeAegisCount(): number {
    return this.aegises.length;
  }

  /** Live looping ground fields (test/e2e observability). */
  get activeFieldCount(): number {
    return this.fields.length;
  }

  /** Queued not-yet-fired scheduled steps (test/e2e observability). */
  get pendingCount(): number {
    return this.pending.length;
  }

  /** Total live overlay objects across all effect pools. */
  get activeCount(): number {
    return (
      this.activeParticleCount +
      this.activeTracerCount +
      this.activePulseCount +
      this.activeProjectileCount +
      this.activeImpactCount +
      this.activeAegisCount +
      this.activeFieldCount
    );
  }

  /** Apply the current presentation tier's scheduler budget. */
  setPendingLimit(limit: number): void {
    this.pendingLimit = Math.max(0, Math.min(FX_MAX_PENDING, Math.floor(limit)));
    const rank = { primary: 0, secondary: 1, decorative: 2 } as const;
    while (this.pending.length > this.pendingLimit) {
      let dropIndex = -1;
      for (let index = this.pending.length - 1; index >= 0; index -= 1) {
        if (this.pending[index]!.priority === 'primary') continue;
        if (
          dropIndex < 0 ||
          rank[this.pending[index]!.priority] > rank[this.pending[dropIndex]!.priority]
        ) {
          dropIndex = index;
        }
      }
      if (dropIndex < 0) break;
      this.pending.splice(dropIndex, 1);
    }
  }

  /**
   * Late-bind the loaded baked effect strips (they load async at boot). Before
   * this - and for any strip absent from the bake - the sprite effects degrade
   * to the Graphics primitives below, never a crash (ADR-0180's contract).
   */
  setEffectArt(art: EffectArt): void {
    this.effectArt = art;
  }

  /**
   * Late-bind the baked blood atlas textures the **molten** ground look draws
   * from (PRD #419 slice B) - the same load the {@link ./bloodLayer.BloodLayer}
   * decals use, shared, never re-sliced. Before this (or for an unbaked splat)
   * the molten field degrades to a Graphics splat blob, never a crash.
   */
  setBloodArt(textures: BloodTextures): void {
    this.bloodTextures = textures;
  }

  /**
   * Late-bind the **blaze** hue (UO 1161) the molten splat wears over the red
   * blood art. A real luminance-to-palette hue ramp, not a flat
   * tint: a Pixi `.tint` only *multiplies*, so it could never lift the red blood
   * art to fire (it read as "still just blood"); the ramp remaps it the way
   * `bodyHue` recolours a body. Absent this (unbaked hue table / headless test)
   * the splat degrades to the old flat {@link MOLTEN_TINT} wash, never a crash.
   */
  setMoltenHue(hue: number): void {
    this.moltenHue = hue;
  }

  /**
   * Schedule an arbitrary FX step `delayMs` from now on the overlay clock (the
   * volley planner's timeline executor - damage floats ride this too). Fired by
   * {@link update} in insertion order once due. Secondary and decorative work
   * yield first under pressure. Primary work may use a bounded emergency reserve;
   * if that reserve also fills, the oldest primary cue fires early to preserve
   * every cue without unbounded memory growth.
   */
  schedule(
    delayMs: number,
    fire: () => void,
    priority: ArenaFeedbackPriority = 'secondary',
  ): void {
    if (this.pending.length >= this.pendingLimit) {
      const rank = { primary: 0, secondary: 1, decorative: 2 } as const;
      let replacement = -1;
      for (let index = this.pending.length - 1; index >= 0; index -= 1) {
        if (rank[this.pending[index]!.priority] > rank[priority]) {
          replacement = index;
          break;
        }
      }
      if (replacement >= 0) {
        this.pending.splice(replacement, 1);
      } else if (priority !== 'primary') {
        return;
      } else if (this.pending.length >= FX_MAX_PENDING + FX_PRIMARY_PENDING_RESERVE) {
        const oldestPrimary = this.pending.findIndex((step) => step.priority === 'primary');
        if (oldestPrimary >= 0) this.pending.splice(oldestPrimary, 1)[0]!.fire();
      }
    }
    this.pending.push({ atMs: this.clockMs + Math.max(0, delayMs), fire, priority });
  }

  /**
   * Pop a pooled `Graphics` (or create one while the kind's cap allows), returning
   * `undefined` when the kind is at budget - the caller drops the effect silently.
   */
  private acquire(free: Graphics[], liveCount: number, cap: number): Graphics | undefined {
    const pooled = free.pop();
    if (pooled) {
      pooled.visible = true;
      return pooled;
    }
    if (liveCount >= cap) return undefined;
    const gfx = new Graphics();
    this.addChild(gfx);
    return gfx;
  }

  /** Return a spent `Graphics` to its free list (hidden, retained, reused). */
  private release(gfx: Graphics, free: Graphics[]): void {
    gfx.visible = false;
    free.push(gfx);
  }

  /**
   * A radial particle burst at a world-local point (an enemy death). Spawns up to
   * `count` flecks, silently clamped to the remaining particle budget.
   */
  burst(x: number, y: number, color: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const gfx = this.acquire(this.freeParticles, this.particles.length, FX_MAX_PARTICLES);
      if (!gfx) return; // at budget: drop the rest of this burst
      gfx.clear();
      gfx.circle(0, 0, PARTICLE_RADIUS).fill({ color, alpha: 1 });
      gfx.position.set(x, y);
      gfx.alpha = 1;
      gfx.scale.set(1);
      const angle = this.rng() * Math.PI * 2;
      const speed = PARTICLE_SPEED_MIN + this.rng() * (PARTICLE_SPEED_MAX - PARTICLE_SPEED_MIN);
      this.particles.push({
        gfx,
        vx: Math.cos(angle) * speed,
        // Bias upward so the burst pops before gravity rains it down.
        vy: Math.sin(angle) * speed - 40,
        ageMs: 0,
      });
    }
  }

  /**
   * A projectile tracer dot crossing from `from` to `to` (world-local points) over
   * {@link TRACER_TTL_MS}. Dropped silently at the tracer cap.
   */
  tracer(from: FxPoint, to: FxPoint, color: number): void {
    const gfx = this.acquire(this.freeTracers, this.tracers.length, FX_MAX_TRACERS);
    if (!gfx) return;
    gfx.clear();
    gfx.circle(0, 0, TRACER_RADIUS).fill({ color, alpha: 1 });
    gfx.position.set(from.x, from.y);
    gfx.alpha = 1;
    gfx.scale.set(1);
    this.tracers.push({ gfx, from: { ...from }, to: { ...to }, ageMs: 0 });
  }

  /**
   * An expanding aura-pulse ring: a tile diamond growing from the caster to a
   * `radiusTiles` Chebyshev reach, fading as it expands. Dropped at the pulse cap.
   */
  pulse(x: number, y: number, radiusTiles: number, color: number): void {
    const gfx = this.acquire(this.freePulses, this.pulses.length, FX_MAX_PULSES);
    if (!gfx) return;
    gfx.clear();
    gfx.poly(DIAMOND).stroke({ width: 2.5, color, alpha: 1 });
    gfx.position.set(x, y);
    gfx.alpha = 1;
    gfx.scale.set(0.4);
    // A Chebyshev radius r covers a (2r+1)-tile square, which projects to a
    // diamond ~2r times the one-tile cell.
    this.pulses.push({ gfx, targetScale: Math.max(1, radiusTiles * 2), ageMs: 0 });
  }

  /**
   * Draw a broad crescent in front of a melee attacker.
   * Reach and arc width come from the attack definition so future equipment and
   * skill modifiers grow the gameplay sector and its visual together.
   */
  sweep(
    x: number,
    y: number,
    angle: number,
    radiusTiles: number,
    arcDegrees: number,
    color: number,
  ): void {
    const gfx = this.acquire(
      this.freeSweeps,
      this.sweeps.length,
      FX_MAX_SWEEPS,
    );
    if (!gfx) return;
    const radius = Math.max(
      TILE_HALF * 2.4,
      radiusTiles * TILE_HALF * Math.SQRT2,
    );
    const rotation = 0.2;
    const halfArc = Math.max(
      0,
      (arcDegrees * Math.PI) / 360 - rotation,
    );
    gfx.clear();
    gfx
      .arc(0, 0, radius, -halfArc, halfArc)
      .stroke({ width: 13, color, alpha: 0.16 });
    gfx
      .arc(0, 0, radius, -halfArc, halfArc)
      .stroke({ width: 6, color, alpha: 0.78 });
    gfx
      .arc(0, 0, radius - 5, -halfArc * 0.96, halfArc * 0.96)
      .stroke({ width: 2, color: 0xffffff, alpha: 0.9 });
    gfx.position.set(x, y);
    gfx.scale.set(0.78);
    gfx.alpha = 1;
    gfx.rotation = angle + rotation;
    this.sweeps.push({
      gfx,
      startRotation: gfx.rotation,
      rotationDelta: -rotation * 2,
      scaleYRatio: 1,
      ttlMs: SWEEP_TTL_MS,
      ageMs: 0,
    });
  }

  /** Draw Piercing Thrust's full straight damage lane for one quick beat. */
  pierce(
    x: number,
    y: number,
    endX: number,
    endY: number,
    widthPixels: number,
    color: number,
  ): void {
    const gfx = this.acquire(
      this.freeSweeps,
      this.sweeps.length,
      FX_MAX_SWEEPS,
    );
    if (!gfx) return;
    const width = Math.max(6, widthPixels);
    const length = Math.hypot(endX, endY);
    if (length === 0) {
      this.release(gfx, this.freeSweeps);
      return;
    }
    const point = (progress: number): [number, number] => [
      endX * progress,
      endY * progress,
    ];
    const [startX, startY] = point(0.08);
    const [bodyX, bodyY] = point(0.7);
    const [glintX, glintY] = point(0.42);
    const [tipX, tipY] = point(0.98);
    gfx.clear();
    gfx
      .moveTo(startX, startY)
      .lineTo(endX, endY)
      .stroke({ width, color, alpha: 0.12 });
    gfx
      .moveTo(startX, startY)
      .lineTo(bodyX, bodyY)
      .stroke({ width: 5, color, alpha: 0.48 });
    gfx
      .moveTo(glintX, glintY)
      .lineTo(tipX, tipY)
      .stroke({ width: 2, color: 0xffffff, alpha: 0.88 });
    gfx.position.set(x, y);
    gfx.scale.set(0.82);
    gfx.alpha = 1;
    gfx.rotation = 0;
    this.sweeps.push({
      gfx,
      startRotation: 0,
      rotationDelta: 0,
      scaleYRatio: 1,
      scaleFrom: 0.82,
      scaleTo: 1,
      ttlMs: PIERCE_TTL_MS,
      ageMs: 0,
    });
  }

  /** Draw a broken circular blade trail around a spinning melee attacker. */
  whirlwind(
    x: number,
    y: number,
    radiusTiles: number,
    color: number,
    liveTarget?: () => FxPoint | undefined,
  ): void {
    const gfx = this.acquire(
      this.freeSweeps,
      this.sweeps.length,
      FX_MAX_SWEEPS,
    );
    if (!gfx) return;
    const radius = Math.max(TILE_HALF * 2.5, radiusTiles * TILE_HALF * 1.35);
    const segment = (72 * Math.PI) / 180;
    const gap = (18 * Math.PI) / 180;
    gfx.clear();
    for (let index = 0; index < 4; index++) {
      const start = index * (Math.PI / 2) + gap;
      const end = start + segment;
      gfx
        .arc(0, 0, radius, start, end)
        .stroke({ width: 15, color, alpha: 0.14 });
      gfx
        .arc(0, 0, radius, start, end)
        .stroke({ width: 6, color, alpha: 0.8 });
      gfx
        .arc(0, 0, radius - 5, start + 0.05, end - 0.05)
        .stroke({ width: 2, color: 0xffffff, alpha: 0.92 });
    }
    gfx.position.set(x, y + 5);
    gfx.scale.set(0.72, 0.46);
    gfx.alpha = 1;
    gfx.rotation = 0;
    this.sweeps.push({
      gfx,
      startRotation: 0,
      rotationDelta: 0,
      scaleYRatio: 0.64,
      ttlMs: WHIRLWIND_TTL_MS,
      ageMs: 0,
      liveTarget,
    });
  }

  /** Pop a pooled effect sprite (or create under `cap`); `undefined` at budget. */
  private acquireSprite(liveCount: number, cap: number): UoHueSprite | undefined {
    const pooled = this.freeSprites.pop();
    if (pooled) {
      pooled.visible = true;
      return pooled;
    }
    if (liveCount >= cap) return undefined;
    const sprite = new UoHueSprite();
    this.addChild(sprite);
    return sprite;
  }

  /** Return a spent effect sprite to the shared free list (hidden, retained).
   * The tint and molten blaze hue (PRD #419 slice B) reset here so a
   * molten splat's fire recolour can never leak onto a reused
   * projectile/impact/strip sprite. */
  private releaseSprite(sprite: UoHueSprite): void {
    sprite.visible = false;
    sprite.tint = 0xffffff;
    sprite.blendMode = 'normal';
    sprite.hue = 0;
    sprite.partialHue = false;
    sprite.flashMix = 0;
    sprite.filters = null;
    this.freeSprites.push(sprite);
  }

  /** Point a sprite at one strip frame, pivoting on the frame's baked center. */
  private applyFrame(sprite: Sprite, clip: EffectClip, index: number): void {
    const frame = clip.frames[index]!;
    sprite.texture = frame.texture;
    sprite.pivot.set(frame.cx, frame.cy);
    sprite.blendMode = clip.additive ? 'add' : 'normal';
  }

  /**
   * Launch a projectile effect flying `from` -> `to` (world-local points), the
   * fire mage's arrow/fireball (ADR-0181). Returns the flight duration in ms -
   * computed from distance x {@link projectileFlightMs}'s shared speed - and
   * returns it **even when the sprite is dropped at cap or degraded**, so a
   * caller chaining impacts off the flight never skews with render budget.
   * With no baked strip for `kind`, degrades to the classic tracer dot.
   */
  projectile(
    from: FxPoint,
    to: FxPoint,
    kind: ProjectileEffectId,
    sizeTier: number,
    color: number,
    liveTarget?: () => FxPoint | undefined,
  ): number {
    const flightMs = projectileFlightMs(from, to);
    const clip = this.effectArt[kind];
    if (!clip || clip.frames.length === 0) {
      this.tracer(from, to, color);
      return flightMs;
    }
    const sprite = this.acquireSprite(this.projectiles.length, FX_MAX_PROJECTILES);
    if (!sprite) return flightMs;
    this.applyFrame(sprite, clip, 0);
    const artScale = 1 + PROJECTILE_TIER_SCALE * Math.max(0, sizeTier - 1);
    sprite.scale.set(artScale);
    sprite.alpha = 1;
    sprite.position.set(from.x, from.y);
    // UO effect art rotates along the velocity (fixed per-strip authored orientation).
    sprite.rotation =
      Math.atan2(to.y - from.y, to.x - from.x) + PROJECTILE_ART_BASE_ANGLE_RAD[kind];
    this.projectiles.push({
      sprite,
      clip,
      from: { ...from },
      to: { ...to },
      flightMs,
      artScale,
      frameIndex: 0,
      ageMs: 0,
      kind,
      liveTarget,
    });
    return flightMs;
  }

  /**
   * Play a one-shot impact effect (explosion / flame-strike column) at a
   * world-local point: the strip plays through once at its baked pacing, then
   * releases. With no baked strip, degrades to the Slice F primitives - a
   * burst, plus the expanding ring at `radiusTiles` for the explosion's
   * footprint.
   */
  impact(at: FxPoint, kind: ImpactEffectId, radiusTiles: number, color: number): void {
    const clip = this.effectArt[kind];
    if (!clip || clip.frames.length === 0) {
      this.burst(at.x, at.y, color, BURST_PARTICLES);
      // Blast-shaped impacts (explosion / the Combustion mushroom, PRD #419
      // slice C) keep their footprint readable with the expanding ring.
      if (kind === 'explosion' || kind === 'fire-mushroom') {
        this.pulse(at.x, at.y, radiusTiles, color);
      }
      return;
    }
    const sprite = this.acquireSprite(this.impacts.length, FX_MAX_IMPACTS);
    if (!sprite) return;
    this.applyFrame(sprite, clip, 0);
    sprite.scale.set(1);
    sprite.rotation = 0;
    sprite.alpha = 1;
    sprite.position.set(at.x, at.y);
    this.impacts.push({
      sprite,
      clip,
      ttlMs: clip.frames.length * clip.frameIntervalMs,
      frameIndex: 0,
      ageMs: 0,
    });
  }

  /** Hold the authored defensive sparkle strip around the live Warrior. */
  aegis(
    at: FxPoint,
    ttlMs: number,
    liveTarget?: () => FxPoint | undefined,
    key?: string,
  ): void {
    const sparkleClip = this.effectArt['warrior-aegis-sparkle'];
    if (!sparkleClip?.frames.length) {
      const fallbackY = at.y - TILE_HALF + AEGIS_SPARKLE_OFFSET_Y;
      this.burst(at.x, fallbackY, 0xffef9d, BURST_PARTICLES);
      this.pulse(at.x, fallbackY, 1, 0xffef9d);
      return;
    }
    if (this.aegises.length >= FX_MAX_AEGIS) return;
    const sparkle = this.acquireSprite(this.aegises.length, FX_MAX_AEGIS);
    if (!sparkle) return;
    this.applyFrame(sparkle, sparkleClip, 0);
    sparkle.scale.set(1);
    sparkle.rotation = 0;
    sparkle.alpha = 1;
    sparkle.position.set(
      at.x,
      at.y - TILE_HALF + AEGIS_SPARKLE_OFFSET_Y,
    );
    this.aegises.push({
      key,
      sparkle,
      sparkleClip,
      ttlMs,
      ageMs: 0,
      sparkleFrameIndex: 0,
      liveTarget,
    });
  }

  /** Remove one optimistically presented Aegis after an authoritative rejection. */
  cancelAegis(key: string): void {
    const index = this.aegises.findIndex((aegis) => aegis.key === key);
    if (index < 0) return;
    const [aegis] = this.aegises.splice(index, 1);
    if (!aegis) return;
    this.releaseSprite(aegis.sparkle);
  }

  /**
   * Hold a looping ground effect (fire field / telegraph sparkle, PRD #378) at
   * a world-local point for `durationMs`: the strip loops at its baked pacing
   * for the whole duration, then releases. With no baked strip, degrades to a
   * flickering tinted tile diamond - the footprint stays readable. Dropped
   * silently at the field cap.
   *
   * The **molten** look (PRD #419 slice B) never reads the effect strips: it
   * draws the blood static `art` (the planner's deterministic per-tile pick)
   * tinted {@link MOLTEN_TINT}, held motionless for the duration - residue,
   * not roaring flame. Unbaked, it degrades to a flickering Graphics splat
   * blob in the molten palette.
   */
  field(at: FxPoint, kind: GroundEffectId, durationMs: number, color: number, art = 0): void {
    if (durationMs <= 0) return;
    if (kind === 'molten') {
      this.moltenField(at, durationMs, art);
      return;
    }
    // `caltrop` (archer, ADR-0237) is a GroundEffectId with no baked strip - it
    // always takes the Graphics degrade below (a cold steel diamond).
    const clip = kind === 'caltrop' ? undefined : this.effectArt[kind];
    if (!clip || clip.frames.length === 0) {
      const gfx = this.acquire(this.freeFieldGfx, this.fields.length, FX_MAX_FIELDS);
      if (!gfx) return;
      gfx.clear();
      gfx.poly(DIAMOND).fill({ color, alpha: 1 });
      gfx.position.set(at.x, at.y + TILE_HALF); // ground diamond sits at the tile base, not body height
      gfx.alpha = FIELD_ALPHA;
      gfx.scale.set(1);
      this.fields.push({ sprite: null, clip: null, gfx, ttlMs: durationMs, frameIndex: 0, ageMs: 0 });
      return;
    }
    const sprite = this.acquireSprite(this.fields.length, FX_MAX_FIELDS);
    if (!sprite) return;
    this.applyFrame(sprite, clip, 0);
    sprite.scale.set(1);
    sprite.rotation = 0;
    sprite.alpha = FIELD_ALPHA;
    sprite.position.set(at.x, at.y);
    this.fields.push({ sprite, clip, gfx: null, ttlMs: durationMs, frameIndex: 0, ageMs: 0 });
  }

  /**
   * The molten residue splat (PRD #419 slice B): the baked blood static for
   * `art` recoloured **blaze** (UO hue 1161, via {@link setMoltenHue}),
   * pinned flat on the tile like a floor decal (a static hold in the fields
   * pool - `clip` null, so the update loop neither animates nor flickers it).
   * The hue ramp replaced a flat Pixi tint, which could only darken the red
   * blood art (it read as "still just blood") - the ramp remaps luminance to
   * fire like `bodyHue`. With no hue bound yet it falls back to the old
   * flat tint; with no baked blood texture at all, degrades to a flickering
   * Graphics blob in the molten palette (the {@link ./bloodLayer} fallback
   * convention). Dropped silently at the field cap.
   */
  private moltenField(at: FxPoint, durationMs: number, art: number): void {
    const texture = this.bloodTextures.get(`blood-${art}`);
    if (!texture) {
      const gfx = this.acquire(this.freeFieldGfx, this.fields.length, FX_MAX_FIELDS);
      if (!gfx) return;
      gfx.clear();
      drawMoltenBlob(gfx, art);
      gfx.position.set(at.x, at.y + TILE_HALF); // ground decal at the tile base
      gfx.alpha = FIELD_ALPHA;
      gfx.scale.set(1);
      this.fields.push({ sprite: null, clip: null, gfx, ttlMs: durationMs, frameIndex: 0, ageMs: 0 });
      return;
    }
    const sprite = this.acquireSprite(this.fields.length, FX_MAX_FIELDS);
    if (!sprite) return;
    sprite.texture = texture;
    sprite.pivot.set(texture.width / 2, texture.height / 2); // centre like a decal
    sprite.scale.set(1);
    sprite.rotation = 0;
    sprite.alpha = FIELD_ALPHA;
    // Recolour the red blood art with the real UO blaze hue ramp (luminance->
    // palette, like bodyHue), not a flat tint - a tint multiply only darkens the
    // red art, never lifts it to fire. Both are reset on release so a reused
    // projectile/impact sprite never leaks the molten look.
    if (this.moltenHue) {
      sprite.hue = this.moltenHue;
    } else {
      sprite.tint = MOLTEN_TINT; // no hue ramp bound yet: the old flat wash as a stopgap
    }
    sprite.position.set(at.x, at.y + TILE_HALF);
    this.fields.push({ sprite, clip: null, gfx: null, ttlMs: durationMs, frameIndex: 0, ageMs: 0 });
  }

  /** Advance every live effect by one render frame; `dtMs <= 0` holds steady. */
  update(dtMs: number): void {
    if (dtMs <= 0) return;
    const dt = dtMs / 1000;

    // The scheduler first: fire due steps in insertion order, so a step landing
    // exactly this frame still gets its first age on this same update.
    this.clockMs += dtMs;
    for (let i = 0; i < this.pending.length; ) {
      if (this.pending[i]!.atMs <= this.clockMs) {
        const step = this.pending.splice(i, 1)[0]!;
        step.fire();
      } else {
        i += 1;
      }
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.ageMs += dtMs;
      if (p.ageMs >= PARTICLE_TTL_MS) {
        this.release(p.gfx, this.freeParticles);
        this.particles.splice(i, 1);
        continue;
      }
      p.vy += PARTICLE_GRAVITY * dt;
      p.gfx.position.set(p.gfx.position.x + p.vx * dt, p.gfx.position.y + p.vy * dt);
      const k = p.ageMs / PARTICLE_TTL_MS;
      p.gfx.alpha = 1 - k;
      p.gfx.scale.set(1 - 0.5 * k);
    }

    for (let i = this.tracers.length - 1; i >= 0; i--) {
      const t = this.tracers[i]!;
      t.ageMs += dtMs;
      if (t.ageMs >= TRACER_TTL_MS) {
        this.release(t.gfx, this.freeTracers);
        this.tracers.splice(i, 1);
        continue;
      }
      const k = t.ageMs / TRACER_TTL_MS;
      t.gfx.position.set(t.from.x + (t.to.x - t.from.x) * k, t.from.y + (t.to.y - t.from.y) * k);
      // Hold bright most of the flight, wink out at the end.
      t.gfx.alpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
    }

    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const p = this.pulses[i]!;
      p.ageMs += dtMs;
      if (p.ageMs >= PULSE_TTL_MS) {
        this.release(p.gfx, this.freePulses);
        this.pulses.splice(i, 1);
        continue;
      }
      const k = p.ageMs / PULSE_TTL_MS;
      p.gfx.scale.set(0.4 + (p.targetScale - 0.4) * k);
      p.gfx.alpha = 1 - k;
    }

    for (let i = this.sweeps.length - 1; i >= 0; i--) {
      const sweep = this.sweeps[i]!;
      sweep.ageMs += dtMs;
      if (sweep.ageMs >= sweep.ttlMs) {
        this.release(sweep.gfx, this.freeSweeps);
        this.sweeps.splice(i, 1);
        continue;
      }
      const k = sweep.ageMs / sweep.ttlMs;
      const eased = 1 - (1 - k) ** 3;
      const target = sweep.liveTarget?.();
      if (target) sweep.gfx.position.set(target.x, target.y + 5);
      const scaleFrom = sweep.scaleFrom ?? 0.78;
      const scaleTo = sweep.scaleTo ?? 1.12;
      const scale = scaleFrom + (scaleTo - scaleFrom) * eased;
      sweep.gfx.scale.set(scale, scale * sweep.scaleYRatio);
      sweep.gfx.rotation = sweep.startRotation + sweep.rotationDelta * eased;
      sweep.gfx.alpha = k < 0.35 ? 1 : (1 - k) / 0.65;
    }

    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i]!;
      p.ageMs += dtMs;
      if (p.ageMs >= p.flightMs) {
        this.releaseSprite(p.sprite);
        this.projectiles.splice(i, 1);
        continue;
      }
      // Home onto the live target (ADR-0216): re-resolve its point so the tracer
      // tracks a mover and lands on it. Coasts to the last-known `to` once the
      // target is gone (undefined). Re-aim the art along the updated heading.
      const homed = p.liveTarget?.();
      if (homed) {
        p.to.x = homed.x;
        p.to.y = homed.y;
        p.sprite.rotation =
          Math.atan2(p.to.y - p.from.y, p.to.x - p.from.x) + PROJECTILE_ART_BASE_ANGLE_RAD[p.kind];
      }
      const k = p.ageMs / p.flightMs;
      p.sprite.position.set(p.from.x + (p.to.x - p.from.x) * k, p.from.y + (p.to.y - p.from.y) * k);
      // The strip LOOPS for the whole flight (a fireball burns all the way).
      const frame = Math.floor(p.ageMs / p.clip.frameIntervalMs) % p.clip.frames.length;
      if (frame !== p.frameIndex) {
        p.frameIndex = frame;
        this.applyFrame(p.sprite, p.clip, frame);
        p.sprite.scale.set(p.artScale); // pivot swap keeps scale; reassert for clarity
      }
    }

    for (let i = this.impacts.length - 1; i >= 0; i--) {
      const im = this.impacts[i]!;
      im.ageMs += dtMs;
      if (im.ageMs >= im.ttlMs) {
        this.releaseSprite(im.sprite);
        this.impacts.splice(i, 1);
        continue;
      }
      // One-shot: frames advance once through the strip, clamped to the last.
      const frame = Math.min(
        im.clip.frames.length - 1,
        Math.floor(im.ageMs / im.clip.frameIntervalMs),
      );
      if (frame !== im.frameIndex) {
        im.frameIndex = frame;
        this.applyFrame(im.sprite, im.clip, frame);
      }
    }

    for (let i = this.aegises.length - 1; i >= 0; i--) {
      const aegis = this.aegises[i]!;
      aegis.ageMs += dtMs;
      if (aegis.ageMs >= aegis.ttlMs) {
        this.releaseSprite(aegis.sparkle);
        this.aegises.splice(i, 1);
        continue;
      }
      const target = aegis.liveTarget?.();
      const at = target ?? {
        x: aegis.sparkle.position.x,
        y:
          aegis.sparkle.position.y +
          TILE_HALF -
          AEGIS_SPARKLE_OFFSET_Y,
      };
      const fade = Math.min(1, (aegis.ttlMs - aegis.ageMs) / 300);
      const frame =
        Math.floor(aegis.ageMs / aegis.sparkleClip.frameIntervalMs) %
        aegis.sparkleClip.frames.length;
      if (frame !== aegis.sparkleFrameIndex) {
        aegis.sparkleFrameIndex = frame;
        this.applyFrame(aegis.sparkle, aegis.sparkleClip, frame);
      }
      aegis.sparkle.position.set(
        at.x,
        at.y - TILE_HALF + AEGIS_SPARKLE_OFFSET_Y,
      );
      aegis.sparkle.alpha = fade * (0.72 + Math.sin(aegis.ageMs / 90) * 0.18);
    }

    for (let i = this.fields.length - 1; i >= 0; i--) {
      const f = this.fields[i]!;
      f.ageMs += dtMs;
      if (f.ageMs >= f.ttlMs) {
        if (f.sprite) this.releaseSprite(f.sprite);
        if (f.gfx) this.release(f.gfx, this.freeFieldGfx);
        this.fields.splice(i, 1);
        continue;
      }
      if (f.sprite && f.clip) {
        // The strip LOOPS in place for the whole hold (a fire field keeps burning).
        const frame = Math.floor(f.ageMs / f.clip.frameIntervalMs) % f.clip.frames.length;
        if (frame !== f.frameIndex) {
          f.frameIndex = frame;
          this.applyFrame(f.sprite, f.clip, frame);
        }
      } else if (f.gfx) {
        // Degrade mode: flicker the diamond so the burn reads as live, not paint.
        const k = (f.ageMs % FIELD_FLICKER_MS) / FIELD_FLICKER_MS;
        f.gfx.alpha = FIELD_ALPHA * (0.6 + 0.4 * Math.abs(1 - 2 * k));
      }
    }
  }
}
